import React, { useState } from 'react'
import {
  Popover,
  EmployeeSelect,
  IconButton,
  Button,
  FilterButton,
  Card,
  Flex,
  Confirmation,
  Toaster,
  Icon,
} from '@ui'
import { Classes, Intent, Menu, MenuItem, Position } from '@blueprintjs/core'
import { Dialog } from '@ui'
import { NewTicketForm } from './TicketForm'
import StatusSelector from './StatusSelect'
import Filters from './Filters'
import api, { ITicket } from '@api'
import track from '@track'
import Event from '@track/Event'
import { get } from 'lodash'
import { pluralize } from '@utils'
import { UseTicketsHook } from './hooks/useTickets'
import { ISelection } from '@hooks/useSelection'
import { NavigateFn } from '@reach/router'
import { useFileDownload } from '@hooks'
interface ToolbarProps {
  tickets: UseTicketsHook
  selection: ISelection
  navigate: NavigateFn
}

const Toolbar: React.FunctionComponent<ToolbarProps> = ({
  tickets,
  selection,
  navigate,
}: ToolbarProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const startFileDownload = useFileDownload()
  const filterCount =
    Object.values(tickets.keyValueFilters).filter((value) => value).length +
    (tickets.query ? 1 : 0) +
    (tickets.ticketStatus ? 1 : 0)

  const data = {
    isOpen,
    setIsOpen,
    tickets,
    selection,
    filterCount,
  }
  return (
    <Card.Header style={{ left: -24, marginLeft: -24, background: 'var(--white)' }}>
      <Flex align="center" justify="space-between" style={{ width: '96%', marginLeft: 24 }}>
        {selection.count ? <SelectionButtons {...data} /> : <DefaultButtons {...data} />}
        <div>
          <Button
            iconName="plus"
            data-button="new_ticket"
            text="New Ticket"
            onClick={() => {
              setIsOpen(true)
              track.tickets({
                eventName: Event.NEW_RECORD_DIALOG,
              })
            }}
          />
          <Popover
            minimal
            position={Position.BOTTOM}
            content={
              <Menu>
                <MenuItem
                  text="Download CSV"
                  icon={<Icon style={{ marginTop: 2, width: 22 }} name="download" />}
                  onClick={() => tickets.fetchExportLink().then((url) => startFileDownload(url))}
                />
              </Menu>
            }
          >
            <IconButton
              round={false}
              className="ml2"
              iconName="ellipsis-h"
              tooltip="More actions"
              data-button="more_actions"
            />
          </Popover>
        </div>
        <NewTicketDialog
          isOpen={isOpen}
          closeDialog={() => setIsOpen(false)}
          onSuccess={(ticket: ITicket) => {
            setIsOpen(false)
            tickets.reload()
            navigate(`/tickets?ticketId=${ticket.id}`)
          }}
        />
      </Flex>
    </Card.Header>
  )
}

const DefaultButtons = ({
  tickets,
  filterCount,
}: {
  tickets: UseTicketsHook
  filterCount: number
}) => {
  let helperText = ''
  let clearButton = null

  if (!tickets.isLoading || tickets.isSorting || tickets.isPaging) {
    if (filterCount) {
      helperText = `${tickets.count} filtered record${pluralize(tickets.count)}`
      clearButton = (
        <IconButton
          minimal
          data-button="clear_filters"
          iconName="times"
          tooltip="Clear filters"
          style={{ marginLeft: 4 }}
          onClick={() => {
            tickets.clearFilters()
            track.tickets({
              eventName: Event.CLEAR_ALL_FILTERS,
            })
          }}
        />
      )
    } else {
      helperText = `${tickets.count} record${pluralize(tickets.count)}`
    }
  }

  return (
    <Flex align="center">
      <Popover hasBackdrop content={<Filters {...{ tickets }} />}>
        <FilterButton filterCount={filterCount - (tickets.query ? 1 : 0)} />
      </Popover>
      <Flex align="center" style={{ marginLeft: 10 }}>
        {helperText}
        {clearButton}
      </Flex>
    </Flex>
  )
}

const SelectionButtons = ({
  tickets,
  selection,
}: {
  tickets: UseTicketsHook
  selection: ISelection
}) => {
  let helperText = ''
  let clearButton = null

  if (!tickets.isLoading || tickets.isSorting || tickets.isPaging) {
    if (selection.count) {
      helperText = `${selection.count} selected`
      clearButton = (
        <IconButton
          minimal
          data-button="clear_selection"
          iconName="times"
          tooltip="Clear selection"
          style={{ marginLeft: 4 }}
          onClick={() => {
            selection.clear()
            track.tickets({
              eventName: Event.CLEAR_SELECTION,
            })
          }}
        />
      )
    } else {
      helperText = `${tickets.count} record${pluralize(tickets.count)}`
    }
  }

  return (
    <Flex align="center">
      <EmployeeSelect
        value={null}
        placeholder="Assign to"
        buttonProps={{
          iconName: 'user',
          style: { marginRight: 8 },
        }}
        onChange={(employee) => {
          api.ticket
            .batchUpdate(
              Object.keys(selection.items).filter((id) => selection.items[id]),
              { assignedToId: employee ? employee.id : undefined }
            )
            .then(() =>
              Toaster.success({
                message: `Ticket${pluralize(selection.count)} updated`,
              })
            )
            .then(() => tickets.reload())
            .catch((error) => Toaster.fail({ error }))
          track.tickets({
            eventName: Event.BATCH_ACTION,
            props: {
              actionName: 'Assign to',
              actionValue: get(employee, 'name', null),
              batchCount: selection.count,
            },
          })
        }}
      />
      <StatusSelector
        value={null}
        placeholder="Mark all"
        buttonProps={{
          iconName: 'tag',
          style: { marginRight: 8 },
          'data-button': 'mark_all',
        }}
        onChange={(status) => {
          api.ticket
            .batchUpdate(
              Object.keys(selection.items).filter((id) => selection.items[id]),
              { status: status ? status.value : undefined }
            )
            .then(() =>
              Toaster.success({
                message: `Ticket${pluralize(selection.count)} updated`,
              })
            )
            .then(() => tickets.reload())
            .catch((error) => Toaster.fail({ error }))
          track.tickets({
            eventName: Event.BATCH_ACTION,
            props: {
              actionName: 'Status',
              actionValue: status ? status.label : null,
              batchCount: selection.count,
            },
          })
        }}
      />
      <Confirmation
        text={`Delete selected ticket${pluralize(selection.count)}?`}
        onConfirm={() => {
          api.ticket
            .batchRemove(Object.keys(selection.items).filter((k) => selection.items[k]))
            .then(() => {
              tickets.reload()
            })
            .then(() =>
              Toaster.success({
                message: `Ticket${pluralize(selection.count)} removed`,
              })
            )
            .catch((error) => Toaster.fail({ error }))
          track.tickets({
            eventName: Event.BATCH_ACTION,
            props: {
              actionName: 'Delete',
              actionValue: null,
              batchCount: selection.count,
            },
          })
        }}
      >
        <Button
          data-button="delete_tickets"
          ghost
          intent={Intent.DANGER}
          text="Delete"
          iconName="trash"
        />
      </Confirmation>
      <Flex align="center" style={{ marginLeft: 10 }}>
        {helperText}
        {clearButton}
      </Flex>
    </Flex>
  )
}

interface NewTicketDialogProps {
  isOpen: boolean | undefined
  closeDialog: () => void
  onSuccess: (ticket: ITicket) => void
}

function NewTicketDialog({ isOpen, closeDialog, onSuccess }: NewTicketDialogProps) {
  const [dirty, setDirty] = useState(false)
  return (
    <Dialog
      style={{ width: 700 }}
      canEscapeKeyClose={false}
      canOutsideClickClose={!dirty}
      title="New Ticket"
      usePortal
      isOpen={isOpen}
      isCloseButtonShown={false}
      toolbar={
        !dirty ? (
          <div className="dialog-buttons">
            <IconButton minimal tooltip="Close" iconName="times" onClick={closeDialog} />
          </div>
        ) : (
          <></>
        )
      }
      body={
        <div className={Classes.DIALOG_BODY}>
          <NewTicketForm
            dirty={dirty}
            setDirty={setDirty}
            onCancel={() => {
              setDirty(false)
              closeDialog()
            }}
            onSuccess={(ticket) => {
              setDirty(false)
              onSuccess(ticket)
              Toaster.success({ message: 'Ticket created' })
            }}
          />
        </div>
      }
    />
  )
}
export default Toolbar
